/**
 * Bulk-upload helpers: turning the parsed sheet into the Items API validation request, mapping
 * the API's error fields and messages to this UI's keys and translated text, renaming header-less
 * columns, and building the editable cells of the "rows to correct" table on the review step.
 *
 * Position: used by `BulkUploadModal.tsx` (validation request, error mapping, generic column
 * names) and `UploadValidation.tsx` (`buildRows`, `removeRow`). The part-number search in
 * `InvalidInputWrapper` reuses quick entry's result list and error styles
 * (`Shared/QuickEntry/Helpers/SharedComponents.tsx` → `ProductList`,
 * `Shared/QuickEntry/Helpers/StyledComponents.tsx`).
 *
 * Rules a change must keep:
 * - Every field of a request part is sent as a string. `web-items-api`
 *   `validators/validateItemPartNumberRequest.validator.ts` → `PartNumberRequestValidator` runs
 *   `notEmpty` on `id`, `partNumber`, `quantity` and `quantityUom`, and fails the whole request
 *   with a 500 when one of those arrives as a number.
 * - `mapErrorName` knows the field names the Items API returns from Magenium (`PartName`,
 *   `QuantityUOM`, `PricingUOM`, `Quantity`). The Items API's own validator already returns this
 *   UI's keys (`partNumber`, `quantity`, `quantityUom`), which pass through unchanged.
 * - The cell editors write edits to `invalidDataRef` only, without a state update, so typing does
 *   not re-render the table. "Process Corrections" (`UploadValidation.tsx`) reads them from there.
 *
 * Tests: `unit-tests/shared/BulkUploadModal/BulkUploadHelpers.spec.tsx`.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { APIUrl } from "@enums/apiUrl.enums";
import { Language } from "@ryerson/frontend.common";
import { UploadValidationContent, UploadValidationText } from "./UploadValidationContent";
import { ITheme } from "@ryerson/frontend.theme";
import { useApplication } from "@ryerson/frontend.application";

import { Dropdown, Input } from "@ryerson/frontend.form-controls";
import { css } from "@emotion/react";
import { Typography } from "@ryerson/frontend.typography";
import { Icon } from "@ryerson/frontend.assets";
import { Flex } from "@ryerson/frontend.layout";

import {
	PartValidation,
	PartValidationRes,
	BulkUploadValidationRequest,
	FileMapping,
	WorkBookJson,
} from "./BulkUpload.model";
import { strip, truncate } from "@components/Helper/utilities";
import { MAX_FIELD_LENGTHS } from "@ryerson/frontend.common";
import { ProductList } from "../QuickEntry/Helpers/SharedComponents";
import { Product, getCustomerPartNumber, useOnClickOutside } from "@ryerson/frontend.common";
import { ErrorContainer, ErrorMessage } from "../QuickEntry/Helpers/StyledComponents";
import { useDebounce } from "@ryerson/frontend.common";
import { SEARCH_MIN_LENGTH } from "@components/Search/Helpers/Search.models";
import { getZipCode } from "@components/AccountSettings/Helpers/zipCode";
import { useEcommerce } from "@ryerson/frontend.e-commerce";
import { BulkUploadModalContent } from "./BulkUploadModalContent";
import { isAxiosError } from "@components/Helper/TypeHelpers";
import { getUsersDefaultUomValue } from "@ryerson/frontend.common";
import { DefaultUOMDisplay } from "@ryerson/common.uom";
import _ from "lodash";
/**
 *
 * @param newLocal : part object
 * @param part string of the part to get value
 * @returns formatted string that is truncated and stripped of bad characters
 *
 * Lengths come from `MAX_FIELD_LENGTHS` (`@ryerson/frontend.common`); the `default` of 15 is not
 * reachable through the parameter type. Truncation happens before stripping, so the result can be
 * shorter than the limit.
 */

const formatString = (
	newLocal: PartValidation,
	part: "lineItemNotes" | "customerReferenceNumber" | "lineLevelPurchaseOrderNumber"
): string => {
	let length: number;
	const charsToStrip = /&|\\|\/|<|>|'|"|“|”/g;
	switch (part) {
		case "lineItemNotes":
			length = MAX_FIELD_LENGTHS.LINE_LEVEL_NOTES;
			break;
		case "customerReferenceNumber":
			length = MAX_FIELD_LENGTHS.CUSTOMER_REFERENCE;
			break;
		case "lineLevelPurchaseOrderNumber":
			length = MAX_FIELD_LENGTHS.LINE_LEVEL_PO;
			break;
		default:
			length = 15;
			break;
	}

	let partToFormat = newLocal[part];

	return strip(charsToStrip, truncate(partToFormat, length));
};

/** maps {@link fileContents} objects’ values to
 * ryerson’s columns, as defined by the {@link FileMapping}
 *
 * Each row's `id` is its 0-based index in the file. Mapped values are sent as text: numbers via
 * `toString()`, and a date cell (a `Date`, see `BulkUploadModal.tsx` → `readFile`) as
 * `Date.prototype.toString()` text such as `Sat Nov 05 2022 00:00:00 GMT-0500 (...)`. Both UOMs
 * are upper-cased; reference number, notes and line PO go through `formatString`. A field that is
 * not mapped comes out `undefined`.
 */
export const mapFileContentsToValidationRequest = (
	fileMapping: FileMapping[],
	fileContents: {
		[key: string]: string | number;
	}[],
	language: Language,
	zipCode: string
): BulkUploadValidationRequest => {
	/**change the object key according to the mapping.
	 * if there is no mapping, just return the original KV-pair.
	 * also, add an id to the object
	 */

	const parts: PartValidation[] = fileContents.map((product, index) => {
		const newLocal = Object.fromEntries(
			Object.entries(product)
				.map(([key, value]: [string, number | string]) => {
					const matchingMapping: FileMapping | undefined = fileMapping.find(
						(mapping) => mapping.userColumn === key
					);
					if (matchingMapping) {
						return [
							matchingMapping.value,
							matchingMapping.value !== "quantity"
								? matchingMapping.value === "quantityUom"
									? value?.toString()?.toUpperCase()
									: value.toString()
								: value.toString(),
						];
					} else
						return [
							fileMapping.find((entry) => entry.userColumn === key)?.value || key,
							value,
						];
				})
				.concat([["id", index.toString()]])
		) as PartValidation;
		return {
			id: newLocal.id,
			partNumber: newLocal.partNumber,
			quantity: newLocal.quantity,
			quantityUom: newLocal.quantityUom?.toUpperCase(),
			pricingUom: newLocal.pricingUom?.toUpperCase(),
			customerReferenceNumber: formatString(newLocal, "customerReferenceNumber"),
			lineItemNotes: formatString(newLocal, "lineItemNotes"),
			lineLevelPurchaseOrderNumber: formatString(newLocal, "lineLevelPurchaseOrderNumber"),
			requestedDeliveryDate: newLocal.requestedDeliveryDate,
		};
	});
	return {
		zipCode,
		language,
		parts,
	};
};

/**
 * Builds the request with {@link mapFileContentsToValidationRequest} and posts it to
 * `APIUrl.BULK_UPLOAD` (`web-items-api` `public/validate-item-part-numbers`). Rows the API rejects
 * still arrive through `handleSuccess`, marked `valid: false`; `handleOnFail` receives a failure of
 * the whole request.
 */
export const validateFileMapping = async (
	fileMapping: FileMapping[],
	fileContents: {
		[key: string]: string | number;
	}[],
	language: Language,
	axiosInstance: AxiosInstance,
	zipCode: string,
	handleSuccess: (response: any) => void = () => {},
	handleOnFail: (response: any) => void = () => {}
) => {
	const requestBody = mapFileContentsToValidationRequest(
		fileMapping,
		fileContents,
		language,
		zipCode
	);
	await axiosInstance
		.post(`${APIUrl.BULK_UPLOAD}`, requestBody)
		.then(handleSuccess)
		.catch(handleOnFail);
};

/** Drops a row from the upload (the trash icon on either review table). */
export const removeRow = (
	row: PartValidationRes,
	validatedData: PartValidationRes[],
	setValidatedData: React.Dispatch<React.SetStateAction<PartValidationRes[]>>
) => {
	setValidatedData([...validatedData.filter((currentRow) => currentRow.id !== row.id)]);
};

/**
 * Cells of the "rows to correct" table (`UploadValidation.tsx` → `InvalidRowsTable`), one object
 * per row in `invalidData`, keyed by field. Every field except `id` and `errorMessage` gets an
 * editor (`InvalidInputWrapper` for a row still invalid, `ValidInputWrapper` for one that came back
 * valid); the table shows only the keys in its headers. `id` shows as the 1-based row number.
 * `errorMessage` shows the error, or "Error Resolved" once the row is valid, and nothing while
 * the row in `invalidData` is marked `updatedByUser`. A `trash` cell is appended.
 */
export const buildRows = (
	validatedData: PartValidationRes[],
	setValidatedData: React.Dispatch<React.SetStateAction<PartValidationRes[]>>,
	invalidData: PartValidationRes[],
	invalidDataRef: React.MutableRefObject<PartValidationRes[]>,
	theme: ITheme,
	language: Language
) => {
	const { error, success } = theme.colors.primary;

	// Applies a product picked in the part-number search: updates the row in `validatedData` and
	// replaces `invalidDataRef` with a copy of the whole validated set. Edits typed into other rows
	// that were held only in `invalidDataRef` are not in that copy.
	const resetProduct = (product: PartValidationRes, partNumber: string) => {
		const updatedData = _.cloneDeep(validatedData);
		const rowIndex = validatedData.findIndex((cur) => cur.id === product.id);
		updatedData[rowIndex] = {
			...updatedData[rowIndex],
			partNumber: partNumber,
			productDetails: product.productDetails,
			updatedByUser: true,
		};
		invalidDataRef.current = [...updatedData];
		setValidatedData([...updatedData]);
	};

	return invalidData?.map((rowData) =>
		Object.fromEntries(
			Object.entries(rowData)
				.map(([key, value], index): [string, JSX.Element] => [
					key,
					key !== "errorMessage" && key !== "id" && !rowData.valid ? (
						<>
							<InvalidInputWrapper
								validatedData={validatedData}
								index={index}
								rowData={rowData}
								currentProperty={key}
								theme={theme}
								value={value}
								language={language}
								invalidData={invalidData}
								invalidDataRef={invalidDataRef}
								resetProduct={resetProduct}
							/>
						</>
					) : key !== "errorMessage" && key !== "id" && rowData.valid ? (
						<>
							<ValidInputWrapper
								validatedData={validatedData}
								index={index}
								rowData={rowData}
								currentProperty={key}
								theme={theme}
								value={value}
								language={language}
								invalidData={invalidData}
								invalidDataRef={invalidDataRef}
								resetProduct={resetProduct}
							/>
						</>
					) : key === "errorMessage" && !rowData.updatedByUser ? (
						<>
							<Typography
								size="sm"
								color={!!rowData.errorMessage && !rowData.valid ? error : success}
							>
								{/* A message that parses as a JSON object shows its first value;
								    any other text shows as it is. */}
								{!!rowData.errorMessage && !rowData.valid
									? (function () {
											try {
												return Object.values(JSON.parse(value))[0];
											} catch (error) {
												return value;
											}
										})()
									: UploadValidationContent[language].errorResolved}
							</Typography>
						</>
					) : key === "id" ? (
						<Typography weight="bold" color="header">
							{parseInt(value) + 1}
						</Typography>
					) : (
						<>{null}</>
					),
				])
				.concat([
					[
						"trash",
						<Icon
							size="sm"
							onClick={() => {
								removeRow(rowData, validatedData, setValidatedData);
							}}
							icon={"trash"}
							key={rowData.id}
						/>,
					],
				])
		)
	);
};

/**
 * Props of the two cell editors. `index` is the field's position in the row, used as a React
 * key.
 */
type InputWrapperProps = {
	index: number;
	rowData: PartValidationRes;
	currentProperty: string;
	theme: ITheme;
	invalidData: PartValidationRes[];
	value: string;
	language: Language;
	validatedData: PartValidationRes[];
	invalidDataRef: React.MutableRefObject<PartValidationRes[]>;
	resetProduct: (product: PartValidationRes, partNumber: string) => void;
};

/**
 * Editor for one field of a row the API rejected:
 * - part number, when the error is "Part not found" or "Multiple parts found": a product search
 *   (`GET APIUrl.SEARCH_PRODUCTS`, `web-items-api` `public/items`, with the cart's zip code) once
 *   the text has been stable for 1 s and has at least `SEARCH_MIN_LENGTH` characters. Picking a
 *   product sets the part number to its customer part number, else its SKU;
 * - quantity UOM and pricing UOM: a dropdown. With `productDetails` the options are the product's
 *   (`getUsersDefaultUomValue`); without, every `DefaultUOMDisplay` unit;
 * - anything else: a text input.
 */
const InvalidInputWrapper: React.FC<InputWrapperProps> = ({
	index,
	rowData,
	currentProperty,
	theme,
	value,
	language,
	invalidData,
	invalidDataRef,
	resetProduct,
}) => {
	const {
		axiosInstance,
		zipCode,
		user: { isLoggedIn },
	} = useApplication();

	const { background } = theme.colors.primary;
	const { text } = theme.colors.secondary;
	const { profile } = useApplication();

	const [inputValue, setInputValue] = useState<string>(value);
	const [isValidRow, setIsValidRow] = useState<boolean>(
		(invalidData.find((invalidRow) => rowData.id === invalidRow.id) &&
			rowData.valid &&
			rowData.errorField === currentProperty) ||
			false
	);

	const [displaySearchTerm, setDisplaySearchTerm] = useState<string | number>("");
	const [showDropdown, setShowDropdown] = useState<boolean>(false);
	const [searchResult, setSearchResult] = useState<Array<Product>>([]);
	const [isError, setIsError] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string | number>("");
	const { selectedShipToAddress, customerPickup, salesPlant } = useEcommerce();
	let debouncedSearchInput: string;
	const DEBOUNCE_TIME = 1000;
	const searchInputContainerRef = useRef<HTMLElement>(null);

	const dropdownOptions = useMemo(() => {
		if (currentProperty !== "quantityUom" && currentProperty !== "pricingUom") return [];
		if (!rowData.productDetails) {
			return [
				{ value: "", display: "" },
				...Object.keys(DefaultUOMDisplay).map((key) => ({
					value: key,
					display: DefaultUOMDisplay[key as keyof typeof DefaultUOMDisplay],
				})),
			];
		}

		const { pricingUomOption, qtyUomOption } = getUsersDefaultUomValue(
			rowData.productDetails,
			profile
		);

		return [
			{ value: "", display: "" },
			...(currentProperty === "quantityUom" ? qtyUomOption : pricingUomOption),
		];
	}, [rowData, currentProperty, profile]);

	debouncedSearchInput = searchTerm.toString();
	const debouncedSearchTerm = useDebounce(debouncedSearchInput, DEBOUNCE_TIME);
	useOnClickOutside([searchInputContainerRef], () => {
		setShowDropdown(false);
	});

	const onChangeText = (event: any): void => {
		const val = event.target.value;
		setSearchTerm(val);
		setDisplaySearchTerm(val);
	};

	const resetToDefault = () => {
		setShowDropdown(false);
		setSearchResult([]);
		setIsError(false);
	};

	// The rows hold display UOMs (`lbs`); the dropdown's values are codes (`LB`). Converts back,
	// leaving any value that is not a display UOM unchanged.
	const getDropDownValue = (currrentValue: string) => {
		if (currentProperty === "quantityUom" || currentProperty === "pricingUom") {
			const UomValue = (
				Object.keys(DefaultUOMDisplay) as Array<keyof typeof DefaultUOMDisplay>
			).find((key) => DefaultUOMDisplay[key] === currrentValue);

			return UomValue || currrentValue;
		}

		return currrentValue;
	};

	useEffect(() => {
		const updatedValue = getDropDownValue(value);
		if (updatedValue !== inputValue) {
			setInputValue(updatedValue);
		}
	}, [value]);

	useEffect(() => {
		setDisplaySearchTerm(rowData.partNumber);
	}, [rowData.partNumber]);

	useEffect(() => {
		const searchTermLength = searchTerm?.toString().length;
		if (searchTermLength < 1 || debouncedSearchTerm !== searchTerm) {
			resetToDefault();
		} else if (searchTermLength >= SEARCH_MIN_LENGTH && debouncedSearchTerm === searchTerm) {
			let inputData = encodeURIComponent(searchTerm.toString().trim());
			let queryParams = `search-phrase=${inputData}`;

			queryParams += `&zip-code=${getZipCode(
				isLoggedIn,
				zipCode,
				selectedShipToAddress,
				customerPickup,
				salesPlant
			)}`;

			axiosInstance
				.get<null, AxiosResponse<Product[]> | AxiosError<Error>>(
					`${APIUrl.SEARCH_PRODUCTS}?${queryParams}`
				)
				.then((response) => {
					if (isAxiosError(response) || response.status !== 200) throw response;
					let responseData = response?.data;
					if (responseData?.length > 0) {
						setSearchResult(responseData);
						setShowDropdown(true);
					} else {
						setIsError(true);
					}
				})
				.catch(() => {
					setIsError(true);
				});
		}
	}, [debouncedSearchTerm, searchTerm]);

	useEffect(() => {
		const updatedRow = invalidData.find((invalidRow) => rowData.id === invalidRow.id);
		if (updatedRow) {
			const updatedValue = getDropDownValue(
				updatedRow[currentProperty as keyof PartValidationRes] as string
			);
			setInputValue(updatedValue);
		}
		setIsValidRow(
			(invalidData.find((invalidRow) => rowData.id === invalidRow.id) && rowData.valid) ||
				false
		);
	}, [invalidData]);

	const onClickSelectItem = (productItem: Product): void => {
		setShowDropdown(false);
		setIsError(false);
		setInputValue(productItem?.partNumber || productItem?.materialMaster);
		setDisplaySearchTerm(productItem?.partNumber || productItem?.materialMaster);
		resetProduct(
			{ ...rowData, productDetails: productItem },
			getCustomerPartNumber(productItem?.altPartNumber) || productItem?.materialMaster
		);
	};

	// Records an edit in `invalidDataRef` only (no state update, so the table does not re-render)
	// and marks the row `updatedByUser`. A quantity is stored as an integer: `parseInt` drops any
	// decimal part, and an empty input becomes `null`.
	const updateInvalidData = (eventData: string | number): void => {
		let updatedData = [...invalidDataRef.current];
		const rowIndex = updatedData.findIndex((cur) => cur.id === rowData.id);
		const updatedProperty =
			currentProperty === "quantity"
				? eventData
					? parseInt(eventData as string)
					: null
				: eventData;
		if (updatedProperty !== updatedData[rowIndex][currentProperty as keyof PartValidationRes]) {
			updatedData[rowIndex] = {
				...updatedData[rowIndex],
				[currentProperty]: updatedProperty,
				updatedByUser: true,
			};
			invalidDataRef.current = [...updatedData];
		}
	};
	// Compares against the translated text `mapErrorMessage` produced, in the current language.
	const isPartNotFound =
		currentProperty === "partNumber" &&
		(rowData.errorMessage === UploadValidationContent[language].errorMessages.partNumber ||
			rowData.errorMessage === UploadValidationContent[language].errorMessages.multipleParts);

	return isPartNotFound ? (
		<Flex
			itemRef={searchInputContainerRef}
			direction="column"
			style={css`
				width: 100%;
				position: relative;
			`}
		>
			<Input
				css={{ marginTop: "-4px" }}
				{...{ tabIndex: 0 }}
				dataTestId={"bulk-upload-part-number"}
				value={displaySearchTerm}
				size={"sm"}
				placeholder="part number"
				language={language}
				shape="square"
				onChange={(e) => onChangeText(e)}
				onFocus={() => setShowDropdown(true)}
			/>

			{showDropdown && (
				<ProductList searchResults={searchResult} handleClick={onClickSelectItem} />
			)}
			{isError && (
				<ErrorContainer>
					<ErrorMessage {...{ bgColor: background, bColor: text }}>
						{BulkUploadModalContent[language].noResultFound.replace(
							"%searchString%",
							searchTerm.toString()
						)}
					</ErrorMessage>
				</ErrorContainer>
			)}
		</Flex>
	) : (
		<>
			{currentProperty === "quantityUom" || currentProperty === "pricingUom" ? (
				<div
					css={css`
						width: 100%;
						margin-top: ${rowData.errorField === currentProperty &&
						!rowData.updatedByUser &&
						!rowData.valid
							? "10px"
							: "0"};
					`}
				>
					<Dropdown
						key={index}
						size="sm"
						shape="square"
						isValid={
							rowData.errorField !== currentProperty ||
							rowData.valid ||
							rowData.updatedByUser
						}
						options={dropdownOptions}
						onChange={(newValue) => {
							setInputValue(newValue);
							updateInvalidData(newValue);
						}}
						value={inputValue}
						language={language}
					/>
				</div>
			) : (
				<Input
					key={index}
					icon={isValidRow ? "success" : undefined}
					iconPosition="right"
					background={isValidRow ? theme.colors.secondary.lightGreen : ""}
					css={css`
						${rowData.errorField === currentProperty &&
						!rowData.updatedByUser &&
						!rowData.valid
							? "margin-top: 10px; "
							: "width: 100%"}
					`}
					isValid={
						rowData.errorField !== currentProperty ||
						rowData.valid ||
						rowData.updatedByUser
					}
					border={true}
					size="sm"
					inputType="text"
					onChange={(event) => {
						setInputValue(event.target.value);
						updateInvalidData(event.target.value);
					}}
					value={inputValue}
					language={language}
				/>
			)}
		</>
	);
};

/**
 * Editor for one field of a row that came back valid after a correction and is still in the
 * corrections table (`UploadValidation.tsx` moves it out 3 s later). A plain text input for every
 * field, with a success icon; edits go to `invalidDataRef` as in {@link InvalidInputWrapper}.
 */
const ValidInputWrapper: React.FC<InputWrapperProps> = ({
	index,
	rowData,
	currentProperty,
	theme,
	value,
	language,
	invalidData,
	invalidDataRef,
}) => {
	const [inputValue, setInputValue] = useState<string>(value);
	const [isValidRow, setIsValidRow] = useState<boolean>(
		(invalidData.find((invalidRow) => rowData.id === invalidRow.id) &&
			rowData.valid &&
			rowData.errorField === currentProperty) ||
			false
	);

	useEffect(() => {
		const updatedRow = invalidData.find((invalidRow) => rowData.id === invalidRow.id);
		if (updatedRow) {
			setInputValue(updatedRow[currentProperty as keyof PartValidationRes] as string);
		}
		setIsValidRow(
			(invalidData.find((invalidRow) => rowData.id === invalidRow.id) && rowData.valid) ||
				false
		);
	}, [invalidData]);

	const updateInvalidData = (eventData: string | number): void => {
		let updatedData = [...invalidDataRef.current];
		const rowIndex = updatedData.findIndex((cur) => cur.id === rowData.id);
		const updatedProperty =
			currentProperty === "quantity"
				? eventData
					? parseInt(eventData as string)
					: null
				: eventData;
		if (updatedProperty !== updatedData[rowIndex][currentProperty as keyof PartValidationRes]) {
			updatedData[rowIndex] = {
				...updatedData[rowIndex],
				[currentProperty]: updatedProperty,
				updatedByUser: true,
			};
			invalidDataRef.current = [...updatedData];
		}
	};

	return (
		<Input
			key={index}
			icon={isValidRow ? "success" : undefined}
			iconPosition="right"
			background={isValidRow ? theme.colors.secondary.lightGreen : ""}
			css={css`
				${rowData.errorField === currentProperty && !rowData.updatedByUser && !rowData.valid
					? "margin-top: 10px;"
					: ""}
			`}
			isValid={
				rowData.errorField !== currentProperty || rowData.valid || rowData.updatedByUser
			}
			border={true}
			size="sm"
			inputType="text"
			onChange={(event) => {
				setInputValue(event.target.value);
				updateInvalidData(event.target.value);
			}}
			value={inputValue}
			language={language}
		/>
	);
};

/** True when the API's part-name error says the part number matched more than one product. */
const hasMultiplePartsError = (part: PartValidationRes) => {
	const regex = /multiple parts found/i;
	return regex.test(part.errorMessage || "");
};

/**
 * The API's `errorField` as this UI's field key, so the review step can flag the right cell.
 * Magenium's names (`PartName`, `QuantityUOM`, `PricingUOM`, `Quantity`) are translated; any other
 * value, including the Items API validator's own keys, is returned unchanged.
 */
export const mapErrorName = function (part: PartValidationRes): string | null {
	switch (part.errorField) {
		case "PartName":
			return "partNumber";
		case "QuantityUOM":
			return "quantityUom";
		case "PricingUOM":
			return "pricingUom";
		case "Quantity":
			return "quantity";
		default:
			return part.errorField;
	}
};

/**
 * The message to show for a rejected row, in the current language where this UI has one.
 * Part-name errors become "Part not found" or "Multiple parts found"; `InvalidInputWrapper` turns
 * the part-number cell into a search for exactly these two texts. `QuantityUOM` and `PricingUOM`
 * errors become this UI's translated `errorMessages.quantityUom` / `errorMessages.pricingUom`. A
 * `Quantity` error keeps the API's text (for example "Quantity has been updated", when the API
 * changed the quantity to a whole number of pieces), falling back to `errorMessages.quantity`
 * when the API sent none. Apart from `Quantity`, only the fields this function does not name keep
 * the API's text, untranslated.
 */
export const mapErrorMessage = (
	part: PartValidationRes,
	uploadValidationContent: UploadValidationText
): any => {
	switch (part.errorField) {
		case "PartName":
			return hasMultiplePartsError(part)
				? uploadValidationContent.errorMessages.multipleParts
				: uploadValidationContent.errorMessages.partNumber;
		case "QuantityUOM":
			return uploadValidationContent.errorMessages.quantityUom;
		case "PricingUOM":
			return uploadValidationContent.errorMessages.pricingUom;
		case "Quantity":
			return part.errorMessage ?? uploadValidationContent.errorMessages.quantity;
		default:
			return part.errorMessage;
	}
};
/**
 * @description - getColumnLetter
 * @param colIndex - 0-based column index
 *  returns string example "Column A" through "Column ZZ" (index 701)
 *  NOTES: Max index is 701 (702 columns); from 702 on the first character runs past "Z"
 */

const getColumnLetter = (colIndex: number): string => {
	const asciiOffset = 65;
	const maxColumns = 26;
	const modNumber = colIndex % maxColumns;
	const charCode = modNumber + asciiOffset;
	let iteration = Math.floor(colIndex / maxColumns);

	let columnLetters =
		(!!iteration ? String.fromCharCode(iteration - 1 + asciiOffset) : "") +
		String.fromCharCode(charCode);

	return `Column ${columnLetters}`;
};

/**
 * @describe changeToUseGenericColumnNames
 * @param rowObject:WorkBookJson
 * Note: will mutate object inside array and will create new key names
 *
 * Renames every row's keys to `Column A`, `Column B`, ... by their position among the first row's
 * keys, for a sheet without a header row. Called by `BulkUploadModal.tsx` → `readFile`.
 */

export const changeToUseGenericColumnNames = (rowObject: WorkBookJson): WorkBookJson => {
	const headerKeys = Object.keys(rowObject[0]);

	rowObject = rowObject.map((currentRow, i) => {
		let rowKeys = Object.keys(currentRow);
		rowKeys.forEach((curKey) => {
			const colIndex = headerKeys.findIndex((key) => key === curKey);
			if (colIndex > -1) {
				let newKeyName = getColumnLetter(colIndex);
				// write with newkeyName
				delete Object.assign(currentRow, {
					[newKeyName]: currentRow[curKey as any],
				})[curKey];
			}
		});
		return currentRow;
	});

	return rowObject;
};
